import React, { useState } from 'react'
import MetricValues from './MetricValues'

const CAUTIOUS_PHASE_LABELS = {
  testing: (c) => `Cautious: testing ${c.batch_percent}%`,
  stalled_no_analytics: () => 'Cautious: waiting for clicks',
  draining: () => 'Cautious: finishing the test batch',
  sending_rest: () => 'Cautious: sending the rest',
  stopped: () => 'Cautious: stopped',
};

const CAUTIOUS_PHASE_HINTS = {
  testing: (c) => `Test batch of ${c.batch_percent}% sent — waiting before the rest`,
  stalled_no_analytics: () =>
    'Waiting for click data from the short link before checking the thresholds',
  draining: () => 'Waiting for the test batch to finish sending',
  sending_rest: () => 'Thresholds passed — the rest of the audience is going out',
};

const CampaignCard = ({ campaign, currentUser, onStatusChange, onNameChange }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [draftName, setDraftName] = useState(campaign.name || '')
  const [isSavingName, setIsSavingName] = useState(false)
  const [nameError, setNameError] = useState('')

  const handleRename = async (event) => {
    event.preventDefault()
    if (isSavingName || !draftName.trim()) return
    setIsSavingName(true)
    setNameError('')
    try {
      const response = await fetch(`/campaigns/${campaign.id}/rename`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ name: draftName.trim() }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Could not rename campaign.')
      if (onNameChange) onNameChange(campaign.id, data.name)
      setIsRenaming(false)
    } catch (error) {
      setNameError(error.message || 'Could not rename campaign.')
    } finally {
      setIsSavingName(false)
    }
  }

  const { total, sent, failed, pending } = campaign.stats
  const sentPercentage = total > 0 ? Math.round((sent / total) * 100) : 0
  // Получатели старой кампании удалены очисткой, а итоги тогда ещё не сохранялись.
  const purged = !!campaign.details_purged
  const recipientsLabel = purged ? 'recipients not kept' : `${total} ${total === 1 ? 'recipient' : 'recipients'}`

  // Добавляем отображение кликов, если они есть
  const showClicks = campaign.has_analytics && campaign.total_clicks !== null
  const totalClicks = campaign.total_clicks || 0
  const isDepositGoal = campaign.goal_type === 'deposit'
  const conversion = campaign.conversion || {}
  const isEmail = campaign.channel === 'email'

  // Форматирование статуса
  const statusMap = {
    pending: {
      label: 'Pending',
      color: 'bg-slate-100 text-slate-600',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    scheduled: {
      label: 'Scheduled',
      color: 'bg-purple-50 text-purple-600',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    running: {
      label: 'Sending',
      color: 'bg-blue-50 text-blue-600',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M13 10V3L4 14h7v7l9-11h-7z'
          />
        </svg>
      ),
    },
    stopped_by_threshold: {
      label: 'Stopped by threshold',
      color: 'bg-rose-50 text-rose-600',
      icon: (
        <svg className='w-3.5 h-3.5 mr-1' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.5 0L3.2 16.25A2 2 0 005 19z'
          />
        </svg>
      ),
    },
    paused: {
      label: 'Paused',
      color: 'bg-amber-50 text-amber-600',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    completed: {
      label: 'Completed',
      color: 'bg-emerald-50 text-emerald-600',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M5 13l4 4L19 7'
          />
        </svg>
      ),
    },
    canceled: {
      label: 'Canceled',
      color: 'bg-slate-100 text-slate-500',
      icon: (
        <svg
          className='w-3.5 h-3.5 mr-1'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636'
          />
        </svg>
      ),
    },
  }

  const status = campaign.status
  const statusInfo = statusMap[status] || statusMap.pending

  const handlePause = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/v1/campaigns/${campaign.id}/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })
      if (response.ok) {
        const data = await response.json()
        if (onStatusChange) onStatusChange(campaign.id, data.status)
      }
    } catch (error) {
      console.error('Error pausing campaign:', error)
    }
    setIsLoading(false)
  }

  const cautiousAction = async (action) => {
    const res = await fetch(`/api/v1/campaigns/${campaign.id}/${action}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
    });
    const data = await res.json();
    if (res.ok && onStatusChange) onStatusChange(campaign.id, data.status);
  };

  const resumeCautious = () => cautiousAction('resume_cautious');
  const finishCautious = () => cautiousAction('finish_cautious');

  const handleResume = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/v1/campaigns/${campaign.id}/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })
      if (response.ok) {
        const data = await response.json()
        if (onStatusChange) onStatusChange(campaign.id, data.status)
      }
    } catch (error) {
      console.error('Error resuming campaign:', error)
    }
    setIsLoading(false)
  }

  const handleCancel = async () => {
    if (
      !window.confirm(
        'Cancel this scheduled campaign? It will not be sent.'
      )
    ) {
      return
    }
    setIsLoading(true)
    try {
      await fetch(`/api/v1/campaigns/${campaign.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })
      window.location.reload()
    } catch (error) {
      console.error('Error canceling campaign:', error)
      setIsLoading(false)
    }
  }

  return (
    <div className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow transition-all duration-200 ${isEmail ? 'campaign-card-email' : ''}`}>
      <div className='px-4 py-3'>
        {/* Верхняя строка: название, статус */}
        <div className='flex items-center mb-3'>
          <h3 className='text-base font-medium text-slate-900 truncate mr-2'>
            <a
              href={`/campaigns/${campaign.id}`}
              className='hover:text-blue-600'
            >
              {campaign.name}
            </a>
          </h3>
          <button
            type='button'
            className='text-xs text-slate-500 hover:text-blue-600 mr-2'
            aria-label={`Rename campaign ${campaign.name}`}
            onClick={() => {
              setDraftName(campaign.name || '')
              setNameError('')
              setIsRenaming(true)
            }}
          >
            Rename
          </button>
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
              isEmail ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {isEmail ? 'Email' : 'SMS'}
          </span>
          <div
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${statusInfo.color}`}
          >
            {statusInfo.icon}
            {statusInfo.label}
          </div>

          {status === 'running' && (
            <div className='flex items-center ml-2'>
              <span className='relative flex h-2 w-2 mr-1'>
                <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75'></span>
                <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-500'></span>
              </span>
              <span className='text-xs text-emerald-600'>Active</span>
            </div>
          )}
        </div>

        {/* Информация о кампании в одну линию */}
        {isRenaming && (
          <form onSubmit={handleRename} className='mb-3'>
            <div className='flex items-center gap-2'>
              <input
                autoFocus
                aria-label='Campaign name'
                className='flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-sm'
                value={draftName}
                disabled={isSavingName}
                onChange={(event) => setDraftName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && !isSavingName) setIsRenaming(false)
                }}
              />
              <button type='submit' disabled={isSavingName || !draftName.trim()}
                className='text-sm text-blue-600 disabled:opacity-50'>
                {isSavingName ? 'Saving…' : 'Save'}
              </button>
              <button type='button' disabled={isSavingName}
                className='text-sm text-slate-500' onClick={() => setIsRenaming(false)}>
                Cancel
              </button>
            </div>
            {nameError && <p role='alert' className='mt-1 text-sm text-red-600'>{nameError}</p>}
          </form>
        )}

        <div className={isEmail ? 'campaign-card-email__row' : 'flex items-center justify-between gap-3 mb-2'}>
          <div className={isEmail ? 'campaign-card-email__meta' : 'flex items-center min-w-0 w-[24%]'}>
            <div className='text-xs text-slate-500 mr-4 truncate'>
              {campaign.sender_name || 'No sender'}
            </div>

            {campaign.manual_ids ? (
              <div className='text-xs text-violet-600 truncate'>
                Manual List • {recipientsLabel}
              </div>
            ) : (
              <div className='text-xs text-blue-600 truncate'>
                {(campaign.geos || []).length
                  ? campaign.geos.length > 2
                    ? `${campaign.geos.slice(0, 2).join(', ')} +${campaign.geos.length - 2}`
                    : campaign.geos.join(', ')
                  : campaign.country}{' '}
                • {recipientsLabel}
              </div>
            )}
          </div>

          {/* Средняя часть: прогресс и статистика */}
          <div className={isEmail ? 'campaign-card-email__stats' : 'flex items-center justify-center flex-wrap gap-y-1 gap-x-6 flex-1 min-w-0'}>
            {purged && (
              <div
                className='text-xs text-slate-400'
                title='Recipient details are removed 30 days after creation. This campaign was cleaned up before totals were kept.'
              >
                Delivery details archived
              </div>
            )}

            {/* Прогресс */}
            {!purged && <div className='flex items-center'>
              <div className='text-xs text-slate-500 mr-1'>Progress</div>
              <div className='text-xs text-slate-500 ml-1'>
                {sentPercentage}%
              </div>
            </div>}

            {/* Статистика */}
            <div className='flex items-center flex-wrap gap-y-1 gap-x-3'>
              {!purged && !isEmail && (
                <>
                  <div className='flex items-center'>
                    <div className='w-2 h-2 rounded-full bg-emerald-500 mr-1.5'></div>
                    <span className='text-xs text-slate-700'>{sent}</span>
                    <span className='text-xs text-slate-500 ml-1'>sent</span>
                  </div>

                  <div className='flex items-center'>
                    <div className='w-2 h-2 rounded-full bg-blue-500 mr-1.5'></div>
                    <span className='text-xs text-slate-700'>{pending}</span>
                    <span className='text-xs text-slate-500 ml-1'>pending</span>
                  </div>
                </>
              )}

              {isEmail && <MetricValues compact metrics={[
                ...(campaign.metric_catalog || []).filter((metric) => !['depositors','deposits_count','deposit_sum','avg_deposit','median_deposit','median_deposits_count','median_deposit_sum','conversion_rate'].includes(metric.key)),
                ...(pending > 0 ? [{key:'pending',label:'Pending',value:pending,format:'number',hint:'Messages waiting to be sent.'}] : []),
                ...(failed > 0 ? [{key:'sending_errors',label:'Sending errors',value:failed,format:'number',hint:'Sending failures before provider acceptance; separate from Bounce.'}] : []),
              ]} />}

              {!isEmail && showClicks && (
                <div className='flex items-center'>
                  <div className='w-2 h-2 rounded-full bg-indigo-500 mr-1.5'></div>
                  <span className='text-xs text-slate-700'>{totalClicks}</span>
                  <span className='text-xs text-slate-500 ml-1' title='Total Rebrandly transitions; unique recipients are unavailable'>link transitions</span>
                </div>
              )}

              {isDepositGoal && (
                <>
                  <div className='flex items-center'>
                    <div className='w-2 h-2 rounded-full bg-emerald-600 mr-1.5'></div>
                    <span className='text-xs text-slate-700'>{conversion.depositors || 0}</span>
                    <span className='text-xs text-slate-500 ml-1'>depositors</span>
                  </div>
                  <div className='flex items-center'>
                    <div className='w-2 h-2 rounded-full bg-emerald-700 mr-1.5'></div>
                    <span className='text-xs text-slate-700'>${conversion.deposit_sum || 0}</span>
                    <span className='text-xs text-slate-500 ml-1'>deposits</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={`flex items-center justify-end shrink-0 whitespace-nowrap ${isEmail ? 'campaign-card-email__actions' : ''}`}>
            {campaign.cautious_phase &&
              ['running', 'paused', 'stopped_by_threshold'].includes(status) && (
              <span
                className='px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 mr-2'
                title={
                  CAUTIOUS_PHASE_HINTS[campaign.cautious_phase]?.(campaign) ||
                  campaign.stop_reason ||
                  'Cautious sending'
                }
              >
                {CAUTIOUS_PHASE_LABELS[campaign.cautious_phase]?.(campaign) ||
                  `Cautious: ${campaign.cautious_phase.replace(/_/g, ' ')}`}
              </span>
              )}

            {status === 'running' && (
              <button
                onClick={handlePause}
                disabled={isLoading}
                className={`p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors ${
                  isLoading ? 'opacity-50' : ''
                }`}
                title='Pause Campaign'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </button>
            )}

            {status === 'stopped_by_threshold' && (
              <>
                <button
                  onClick={resumeCautious}
                  title={
                    campaign.stop_reason
                      ? `${campaign.stop_reason} — send to the rest of the audience anyway`
                      : 'Continue sending to the rest of the audience'
                  }
                  className='px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                >
                  Continue
                </button>
                <button
                  onClick={finishCautious}
                  title='Finish the campaign — the rest of the audience will not be messaged'
                  className='px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 ml-1'
                >
                  Finish
                </button>
              </>
            )}

            {status === 'paused' && (
              <button
                onClick={handleResume}
                disabled={isLoading}
                className={`p-1.5 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors ${
                  isLoading ? 'opacity-50' : ''
                }`}
                title='Resume Campaign'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </button>
            )}

            {status === 'scheduled' && (
              <>
                <a
                  href={`/campaigns/${campaign.id}/edit`}
                  className='p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors'
                  title='Edit Campaign'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                    />
                  </svg>
                </a>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className={`p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors ml-1 ${
                    isLoading ? 'opacity-50' : ''
                  }`}
                  title='Cancel Scheduled Campaign'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </>
            )}

            {currentUser && currentUser.role === 'admin' && (
              <a
                href={`/campaigns/${campaign.id}/duplicate`}
                className='p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors ml-1'
                title='Duplicate Campaign'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                  />
                </svg>
              </a>
            )}

            {currentUser && currentUser.role === 'admin' && (
              <a
                href={`/campaigns/${campaign.id}/recipients`}
                className='p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors ml-1'
                title='Export recipients (CSV)'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                  />
                </svg>
              </a>
            )}

            {currentUser && currentUser.role === 'admin' && campaign.goal_type === 'deposit' && (
              <a
                href={`/campaigns/${campaign.id}/converted`}
                className='p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors ml-1'
                title='Export converted (CSV)'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 8v2m0-10a8 8 0 100 16 8 8 0 000-16z'
                  />
                </svg>
              </a>
            )}

            {currentUser && currentUser.role === 'admin' && (
              <a
                href={`/campaigns/${campaign.id}`}
                className='inline-flex items-center p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors ml-1'
                title='View Campaign Details'
              >
                <svg
                  className='w-4 h-4 mr-1'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                  />
                </svg>
                <span className='text-xs'>Details</span>
              </a>
            )}
          </div>
        </div>

        {/* Визуальный прогрессбар */}
        <div className='h-2 bg-slate-100 rounded-full overflow-hidden'>
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'
            }`}
            style={{ width: `${sentPercentage}%` }}
          ></div>
        </div>

        {/* Время создания або запланування */}
        <div className='text-xs text-slate-500 mt-2'>
          {status === 'scheduled' && campaign.scheduled_at ? (
            <span className='text-purple-600'>
              Scheduled for {new Date(campaign.scheduled_at).toLocaleString()}
            </span>
          ) : (
            `Created ${campaign.created_at_ago}`
          )}
        </div>
      </div>
    </div>
  )
}

export default CampaignCard
